import React from 'react';
import { Helmet } from 'react-helmet-async';
import SeoHead from '../../../shared/seo/SeoHead';
import { FringesPageSeo } from '../seo/FringesPageSeo';
import '../styles/pompoms-page.css';

const FAQ_ICON = (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 15h-2v-2h2zm0-4h-2V7h2z" />
    </svg>
);

const TOGGLE_ICON = (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
        <path d="M12 5v14M5 12h14" />
    </svg>
);

const PRODUCT_TYPES = [
    {
        emoji: '🌼',
        name: 'Pom Pom Fringe Trim',
        desc: 'Our signature style — a continuous strip of handmade pom poms attached to a woven header tape. Each pom pom is individually tied and trimmed for a uniform, full-round profile. Available in faux fur, wool, and acrylic in 200+ colours.',
        tags: ['3 cm – 8 cm Pom Poms', '200+ Colors', 'Best Seller', 'Per Metre'],
    },
    {
        emoji: '🪡',
        name: 'Silk Thread Fringe',
        desc: 'Lustrous multi-strand silk thread fringe with a smooth, high-sheen cascade. Ideal for sarees, dupattas, lehengas, curtains, and luxury fashion accessories. Available in single, two-tone, and ombré colour options.',
        tags: ['5 cm – 25 cm Length', 'Silk Sheen', 'Bridal Range', 'Per Metre'],
    },
    {
        emoji: '🧶',
        name: 'Wool Pom Pom Fringe',
        desc: 'Classic handspun wool pom poms on a cotton header tape — a timeless boho trim for scarves, bags, blankets, cushion covers, and winter fashion accessories. Dense, matte texture with natural warmth.',
        tags: ['3 cm – 6 cm Pom Poms', 'Natural Fiber', 'Boho Style', 'Per Metre'],
    },
    {
        emoji: '✨',
        name: 'Metallic Fringe',
        desc: 'Gold, silver, rose gold, and bronze metallic lurex fringe for festive lehengas, curtain tiebacks, event décor, and premium fashion trims. Catches light beautifully — a signature detail for bridal and party collections.',
        tags: ['Gold / Silver', 'Rose Gold', 'Festive & Bridal', 'Per Metre'],
    },
    {
        emoji: '🌺',
        name: 'Faux Fur Pom Pom Fringe',
        desc: 'Ultra-fluffy faux fur pom poms on a continuous header tape — 100% cruelty-free. A premium trim for jacket hems, boot tops, bags, and luxury fashion accessories. Extra-dense pile gives maximum volume per pom pom.',
        tags: ['4 cm – 10 cm Pom Poms', 'Cruelty-Free', 'Luxury Fashion', 'Per Metre'],
    },
    {
        emoji: '🎀',
        name: 'Multi-Strand Knotted Fringe',
        desc: 'Dense multi-strand fringe with macramé-style knotted tops — ideal for curtain edges, wall hangings, cushion trims, and bohemian home textile applications. Supplied by the metre in custom lengths and colours.',
        tags: ['8 cm – 30 cm Length', 'Knotted Top', 'Home Décor', 'Per Metre'],
    },
];

const SPECS = [
    { icon: '📏', label: 'Pom Pom Size', value: '2 cm – 10 cm per pom pom on fringe tape' },
    { icon: '📐', label: 'Fringe Length', value: '5 cm – 30 cm strand length (custom on request)' },
    { icon: '🎨', label: 'Color Range', value: '200+ standard colors + Pantone custom matching' },
    { icon: '🧵', label: 'Materials', value: 'Faux fur, wool, silk, acrylic, metallic lurex, cotton' },
    { icon: '📦', label: 'Wholesale MOQ', value: 'From 10 metres per color / style' },
    { icon: '🚢', label: 'Global Shipping', value: 'DHL, FedEx, Airmail — 50+ countries' },
];

const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
        {
            '@type': 'Question',
            name: 'What are pom pom fringes?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Pom pom fringes are decorative trim made from handmade pom poms attached at regular intervals to a woven header tape. The header tape is sewn or glued onto a garment, accessory, or home textile item, creating a row of fluffy pom poms along an edge. Arte Craft India produces pom pom fringes with faux fur, wool, acrylic, and metallic pom poms — all handmade and hand-trimmed in our New Delhi workshop for consistent shape and density.',
            },
        },
        {
            '@type': 'Question',
            name: 'What types of fringes does Arte Craft India make?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Arte Craft India manufactures six main categories of fringes: Pom Pom Fringe Trim (continuous pom poms on header tape), Silk Thread Fringe (lustrous multi-strand for bridal and ethnic wear), Wool Pom Pom Fringe (natural, boho-style trim), Metallic Fringe (gold, silver, rose gold for festive collections), Faux Fur Pom Pom Fringe (ultra-fluffy cruelty-free trim for fashion), and Multi-Strand Knotted Fringe (macramé-style for home décor). All types are available in custom colours and lengths by the metre.',
            },
        },
        {
            '@type': 'Question',
            name: 'What materials are used to make fringes?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Arte Craft India uses premium materials for each fringe type: faux fur (synthetic acrylic/polyester pile — cruelty-free, for fluffy pom pom fringe), wool yarn (natural, warm texture for boho and winter trims), silk thread (lustrous high-sheen for bridal and ethnic fashion), metallic lurex (gold, silver, rose gold, bronze for festive fringes), acrylic yarn (vibrant colour retention, budget-friendly for craft use), and cotton thread (natural, breathable for children\'s accessories). Header tapes are woven cotton or polyester.',
            },
        },
        {
            '@type': 'Question',
            name: 'What sizes and lengths of fringes are available?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Pom pom sizes on fringe tape range from 2 cm mini pom poms to 10 cm large pom poms. Strand fringe lengths range from 5 cm (short trim) to 30 cm (statement fringe). Fringes are sold by the metre — minimum order 10 metres per colour. Custom pom pom sizes, strand lengths, and header tape widths are available for wholesale orders. Please specify pom pom diameter, total fringe drop length, and header tape width when requesting a quote.',
            },
        },
        {
            '@type': 'Question',
            name: 'What are pom pom fringes used for?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Pom pom fringes and decorative fringes are used for: garment hem and cuff trims (jackets, kimonos, cardigans), ethnic wear (sarees, lehengas, kurta borders, dupattas), bag and clutch edge trims, scarf and shawl fringe borders, cushion cover and throw blanket trims, curtain and blind bottom edges, lampshade trims, wall hangings and macramé art, boot and shoe tops, festive and event décor, and gift wrapping and hamper decoration.',
            },
        },
        {
            '@type': 'Question',
            name: 'Can I order custom color fringes?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes — custom colour fringes are one of Arte Craft India\'s most requested services. We offer 200+ standard colours and full Pantone colour matching for wholesale orders. To order: (1) Share your Pantone code, fabric swatch, or reference image. (2) We produce a colour-match sample within 5–7 business days. (3) You approve the sample. (4) Production begins. Minimum 10 metres per custom colour. Both the pom poms and the header tape can be matched to coordinating colours.',
            },
        },
        {
            '@type': 'Question',
            name: 'What is the minimum order quantity (MOQ) for wholesale fringes?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Fringes are sold by the metre. The wholesale minimum order is 10 metres per colour and style for standard catalogue fringes. For custom Pantone colours or non-standard sizes, the MOQ is 20 metres. Volume price breaks are offered at 20m, 50m, 100m, 250m, and 500m+. Private label and custom packaging (rolls, polybags with header cards) are available for orders of 100m+. Contact us for a full wholesale pricing sheet.',
            },
        },
        {
            '@type': 'Question',
            name: 'How do I attach pom pom fringe to fabric or a garment?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Pom pom fringe is attached using the header tape: (1) Sew-on (most durable) — machine stitch or hand stitch along the top edge of the header tape onto the garment or textile. Use a zipper foot to sew close to the base of the pom poms without catching them. (2) Iron-on (for non-washable applications) — peel-and-stick or heat-bond adhesive strips can be applied to the header tape for quick attachment to curtains, bags, and decorative items. (3) Fabric glue — apply along the header tape for craft and décor applications. Always sew for washable garments.',
            },
        },
        {
            '@type': 'Question',
            name: 'How do I care for pom pom fringe on garments?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'For garments with pom pom fringe: (1) Hand wash only in cold water (max 30°C) with mild detergent. (2) Do not machine wash — agitation can tangle fringe strands and pull pom poms from the tape. (3) Gently squeeze — do not wring. (4) Air dry flat; reshape pom poms while damp. (5) Do not iron directly over pom pom fringe; iron from the reverse side. (6) Store folded loosely — do not compress pom pom areas.',
            },
        },
        {
            '@type': 'Question',
            name: 'Do you ship fringes internationally?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes — Arte Craft India ships handmade fringes to 50+ countries worldwide. Fringes are wound onto cardboard rolls and packed in boxes to protect shape in transit. All orders include a tracking number. Shipping options: DHL Express (3–5 business days internationally), FedEx International (4–7 business days), Registered Airmail (10–21 business days, economy). We regularly supply buyers in the USA, UK, Canada, Australia, UAE, Germany, France, and across Europe and South-East Asia.',
            },
        },
        {
            '@type': 'Question',
            name: 'Are your faux fur pom pom fringes cruelty-free?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes — 100% cruelty-free. All faux fur pom pom fringes are made from premium synthetic acrylic and polyester fibres with zero animal content. No real fur, no down, no angora, no animal-derived materials of any kind. Our wool fringes use ethically sourced, mulesing-free natural wool. If strict vegan certification is required for your brand, we recommend the faux fur or acrylic range and can provide material composition documentation on request.',
            },
        },
    ],
};

const productStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Handmade Fringes — Pom Pom Fringe Trim & Decorative Fringes',
    description:
        'Premium handmade fringes manufactured in New Delhi, India. Pom pom fringe trim, silk fringes, wool fringes, metallic fringes, and faux fur fringe. 200+ colours, custom lengths, wholesale sold by the metre. Ships to 50+ countries.',
    brand: { '@type': 'Brand', name: 'Arte Craft India' },
    manufacturer: {
        '@type': 'Organization',
        name: 'Arte Craft India',
        url: 'https://www.artecraftindia.com',
        telephone: '+91-9818548369',
        email: 'info@artecraftindia.com',
        address: {
            '@type': 'PostalAddress',
            streetAddress: '280/13 Gali No. 29 Molarband Extn.',
            addressLocality: 'Badarpur, New Delhi',
            addressRegion: 'Delhi',
            postalCode: '110044',
            addressCountry: 'IN',
        },
        foundingDate: '2017',
    },
    offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'Organization', name: 'Arte Craft India' },
    },
    material: 'Faux Fur, Wool, Silk, Acrylic, Metallic Lurex, Cotton',
    color: '200+ colors available',
    countryOfOrigin: 'India',
};

const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.artecraftindia.com' },
        { '@type': 'ListItem', position: 2, name: 'Products', item: 'https://www.artecraftindia.com/products' },
        { '@type': 'ListItem', position: 3, name: 'Fringes', item: 'https://www.artecraftindia.com/products/fringes' },
    ],
};

const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Handmade Fringes — Pom Pom Fringe Trim | Arte Craft India',
    description:
        'Explore Arte Craft India\'s full range of handmade fringes — pom pom fringe trim, silk fringes, wool, metallic, faux fur, and knotted fringes. Wholesale by the metre from New Delhi.',
    url: 'https://www.artecraftindia.com/products/fringes',
    inLanguage: 'en-US',
    publisher: {
        '@type': 'Organization',
        name: 'Arte Craft India',
        url: 'https://www.artecraftindia.com',
        logo: { '@type': 'ImageObject', url: 'https://www.artecraftindia.com/images/logo.jpg' },
    },
    speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['#fr-hero-h1', '#fr-about-h2', '#fr-faq-h2'],
    },
};

const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Arte Craft India',
    url: 'https://www.artecraftindia.com',
    logo: 'https://www.artecraftindia.com/images/logo.jpg',
    telephone: '+91-9818548369',
    email: 'info@artecraftindia.com',
    foundingDate: '2017',
    address: {
        '@type': 'PostalAddress',
        streetAddress: '280/13 Gali No. 29 Molarband Extn.',
        addressLocality: 'Badarpur, New Delhi',
        addressRegion: 'Delhi',
        postalCode: '110044',
        addressCountry: 'IN',
    },
    description:
        'Arte Craft India is a direct manufacturer of handmade fringes, pom poms, tassels, beaded patches, and textile accessories based in New Delhi, India. Established in 2017, supplying wholesale buyers in 50+ countries.',
};

const FringesPage: React.FC = () => {
    return (
        <div className="pp-page">

            <SeoHead {...FringesPageSeo} />

            <Helmet>
                <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
                <script type="application/ld+json">{JSON.stringify(productStructuredData)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbStructuredData)}</script>
                <script type="application/ld+json">{JSON.stringify(webPageStructuredData)}</script>
                <script type="application/ld+json">{JSON.stringify(organizationStructuredData)}</script>
            </Helmet>

            {/* ── BREADCRUMB ── */}
            <nav aria-label="Breadcrumb" className="pp-breadcrumb-nav">
                <ol className="pp-breadcrumb" itemScope itemType="https://schema.org/BreadcrumbList">
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <a href="/" itemProp="item"><span itemProp="name">Home</span></a>
                        <meta itemProp="position" content="1" />
                    </li>
                    <li><span className="pp-bsep" aria-hidden="true">›</span></li>
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <a href="/products" itemProp="item"><span itemProp="name">Products</span></a>
                        <meta itemProp="position" content="2" />
                    </li>
                    <li><span className="pp-bsep" aria-hidden="true">›</span></li>
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <span itemProp="name" aria-current="page">Fringes</span>
                        <meta itemProp="position" content="3" />
                    </li>
                </ol>
            </nav>

            {/* ── HERO ── */}
            <section className="pp-hero" aria-labelledby="fr-hero-h1">
                <div className="pp-hero-dots" aria-hidden="true">
                    {Array.from({ length: 12 }).map((_, i) => <span key={i} />)}
                </div>
                <div className="pp-hero-inner">
                    <div className="pp-hero-badge" aria-hidden="true">
                        Manufacturer Direct · Est. 2017 · New Delhi, India
                    </div>
                    <h1 id="fr-hero-h1">
                        Handmade{' '}
                        <span className="pp-hero-accent">Fringes</span>
                        <br />
                        Pom Pom Trim · Custom Colors · Wholesale by Metre
                    </h1>
                    <p className="pp-hero-desc speakable-summary">
                        Direct-from-manufacturer fringes — pom pom fringe trim, silk, wool, faux fur &amp;
                        metallic. 200+ colors, custom lengths, sold by the metre. Custom orders and bulk
                        wholesale supplied to 50+ countries since 2017.
                    </p>
                    <div className="pp-hero-stats" role="list" aria-label="Key product highlights">
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">200+</span>
                            <span className="pp-stat-label">Colors</span>
                        </div>
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">By Metre</span>
                            <span className="pp-stat-label">Sold</span>
                        </div>
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">50+</span>
                            <span className="pp-stat-label">Countries</span>
                        </div>
                        <div className="pp-stat" role="listitem">
                            <span className="pp-stat-num">100%</span>
                            <span className="pp-stat-label">Handmade</span>
                        </div>
                    </div>
                    <div className="pp-hero-cta">
                        <a href="/request-a-quote" className="pp-btn pp-btn-gold">
                            Request a Wholesale Quote
                        </a>
                        <a href="/contact" className="pp-btn pp-btn-outline">
                            Contact Us
                        </a>
                    </div>
                </div>
            </section>

            {/* ── TRUST STRIP ── */}
            <div className="pp-trust-strip" aria-label="Why buy from Arte Craft India">
                <div className="pp-trust-inner">
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🤲</div>
                        <div className="pp-trust-text">
                            <strong>100% Handmade</strong>
                            <span>Every pom pom hand-tied</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🎨</div>
                        <div className="pp-trust-text">
                            <strong>Custom Colors</strong>
                            <span>Pantone matching available</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">📦</div>
                        <div className="pp-trust-text">
                            <strong>MOQ 10 Metres</strong>
                            <span>Tiered volume pricing</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🌍</div>
                        <div className="pp-trust-text">
                            <strong>Ships Worldwide</strong>
                            <span>DHL, FedEx, Airmail</span>
                        </div>
                    </div>
                    <div className="pp-trust-item">
                        <div className="pp-trust-icon" aria-hidden="true">🐇</div>
                        <div className="pp-trust-text">
                            <strong>Cruelty-Free Options</strong>
                            <span>100% animal-free faux fur</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── PRODUCT TYPES ── */}
            <main id="main-content" role="main">
                <div className="pp-wrapper">

                    <div className="pp-section-header">
                        <span className="pp-section-label">Our Range</span>
                        <h2>Fringe Collections</h2>
                        <p className="pp-section-sub">
                            Six distinct fringe styles — each crafted for a specific use case,
                            all available in custom colors and wholesale quantities sold by the metre.
                        </p>
                    </div>

                    <div
                        className="pp-types-grid"
                        itemScope
                        itemType="https://schema.org/ItemList"
                        aria-label="Fringe product collections"
                    >
                        {PRODUCT_TYPES.map((pt, index) => (
                            <article
                                key={pt.name}
                                className="pp-type-card"
                                itemScope
                                itemProp="itemListElement"
                                itemType="https://schema.org/ListItem"
                            >
                                <meta itemProp="position" content={String(index + 1)} />
                                <span className="pp-type-emoji" aria-hidden="true">{pt.emoji}</span>
                                <h3 itemProp="name">{pt.name}</h3>
                                <p itemProp="description">{pt.desc}</p>
                                <div className="pp-type-tags" aria-label={`${pt.name} specifications`}>
                                    {pt.tags.map(t => <span key={t} className="pp-tag">{t}</span>)}
                                </div>
                            </article>
                        ))}
                    </div>

                    {/* ── ABOUT / SPECIFICATIONS ── */}
                    <section className="pp-about-grid" aria-labelledby="fr-about-h2">
                        <div className="pp-about-prose">
                            <h2 id="fr-about-h2">
                                India's Premier Handmade Fringe Manufacturer
                            </h2>
                            <p>
                                <strong>Arte Craft India</strong> has been producing handmade fringes and pom pom
                                trim from New Delhi since 2017. We are a <strong>direct manufacturer</strong> — not
                                a middleman — which means every metre of fringe is crafted in our own workshop by
                                skilled artisans, giving you factory-direct quality at the best possible price.
                            </p>
                            <p>
                                Our pom pom fringes are produced on a <strong>continuous woven header tape</strong>
                                with individually hand-tied and hand-trimmed pom poms at uniform intervals. This
                                construction gives a consistent, professional finish whether you need 1 metre for
                                a boutique garment or 500 metres for a wholesale production run.
                            </p>
                            <p>
                                Trusted by fashion designers, garment manufacturers, home textile brands, and
                                accessories labels across the <strong>USA, UK, Canada, Australia, UAE, Europe,
                                and South-East Asia</strong>. We handle everything from small retail quantities
                                to large-volume wholesale contracts with private label packaging.
                            </p>
                        </div>
                        <div className="pp-specs-list" aria-label="Product specifications">
                            {SPECS.map(s => (
                                <div key={s.label} className="pp-spec-row">
                                    <div className="pp-spec-icon" aria-hidden="true">{s.icon}</div>
                                    <div className="pp-spec-text">
                                        <strong>{s.label}</strong>
                                        <span>{s.value}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ── FAQ ── */}
                    <section aria-labelledby="fr-faq-h2" itemScope itemType="https://schema.org/FAQPage">
                        <div className="pp-section-header" style={{ paddingTop: 0 }}>
                            <span className="pp-section-label">Knowledge Base</span>
                            <h2 id="fr-faq-h2">Frequently Asked Questions — Fringes</h2>
                            <p className="pp-section-sub">
                                Everything you need to know before placing your order — from materials and
                                attachment to wholesale pricing and international shipping.
                            </p>
                        </div>

                        {/* ── About Fringes ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            About Fringes &amp; Pom Pom Fringe Trim
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What are pom pom fringes?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Definition</span>
                                        <p itemProp="text">
                                            <strong>Pom pom fringes</strong> are decorative trim made from handmade pom poms
                                            attached at regular intervals to a woven header tape. The header tape is sewn or
                                            glued onto a garment, accessory, or home textile item, creating a continuous row
                                            of fluffy pom poms along an edge or hem.
                                        </p>
                                        <p>
                                            Arte Craft India produces pom pom fringes with faux fur, wool, acrylic, and metallic
                                            pom poms — all <strong>handmade and hand-trimmed</strong> in our New Delhi workshop
                                            for consistent shape, density, and spacing along the tape.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What types of fringes does Arte Craft India make?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Product Range</span>
                                        <p itemProp="text">We manufacture six main fringe styles:</p>
                                        <ul>
                                            <li><strong>Pom Pom Fringe Trim</strong> — Acrylic/faux fur pom poms on header tape; best seller for fashion and craft</li>
                                            <li><strong>Silk Thread Fringe</strong> — Lustrous multi-strand silk cascade for bridal and ethnic wear</li>
                                            <li><strong>Wool Pom Pom Fringe</strong> — Natural wool pom poms for boho and winter accessories</li>
                                            <li><strong>Metallic Fringe</strong> — Gold, silver, rose gold lurex for festive and bridal collections</li>
                                            <li><strong>Faux Fur Pom Pom Fringe</strong> — Ultra-fluffy cruelty-free trim for luxury fashion</li>
                                            <li><strong>Multi-Strand Knotted Fringe</strong> — Macramé-style for curtains and home décor</li>
                                        </ul>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What materials are used to make fringes?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Materials</span>
                                        <p itemProp="text">We use carefully selected materials for each fringe type:</p>
                                        <ul>
                                            <li><strong>Faux Fur</strong> — Synthetic acrylic/polyester pile; hypoallergenic, cruelty-free for fluffy pom pom fringe</li>
                                            <li><strong>Wool Yarn</strong> — Ethically sourced natural wool for warm, matte-textured fringe</li>
                                            <li><strong>Silk Thread</strong> — Lustrous, high-sheen for bridal and ethnic fashion fringe</li>
                                            <li><strong>Metallic Lurex</strong> — Woven metallic filament for gold, silver, and rose gold fringes</li>
                                            <li><strong>Acrylic Yarn</strong> — Vibrant, budget-friendly for standard pom pom fringe trim</li>
                                            <li><strong>Cotton Thread</strong> — Natural, breathable for knotted and macramé-style fringe</li>
                                        </ul>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What sizes and lengths of fringes are available?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Sizes &amp; Lengths</span>
                                        <p itemProp="text">
                                            Pom pom sizes on fringe tape range from <strong>2 cm mini pom poms</strong> to
                                            <strong> 10 cm large pom poms</strong>. Strand fringe lengths range from
                                            <strong> 5 cm</strong> (short trim) to <strong>30 cm</strong> (statement fringe).
                                        </p>
                                        <p>
                                            All fringes are <strong>sold by the metre</strong>. When ordering, please specify:
                                            pom pom diameter, total fringe drop length, header tape width, and colour. Custom
                                            sizes are produced for wholesale orders of 20m+.
                                        </p>
                                    </div>
                                </details>
                            </article>

                        </div>

                        {/* ── Uses & Application ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            Uses &amp; Attachment
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What are pom pom fringes used for?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Applications</span>
                                        <p itemProp="text">Pom pom fringes and decorative fringes are used for:</p>
                                        <ul>
                                            <li><strong>Garment hems &amp; cuffs</strong> — jackets, kimonos, cardigans, and festival wear</li>
                                            <li><strong>Ethnic fashion</strong> — saree borders, lehenga hems, dupatta edges, and kurta trims</li>
                                            <li><strong>Bags &amp; clutches</strong> — bottom edge and zip-line fringe accents</li>
                                            <li><strong>Scarves &amp; shawls</strong> — end fringe on wraps and stoles</li>
                                            <li><strong>Cushion covers &amp; throws</strong> — edge trim on home textiles</li>
                                            <li><strong>Curtains &amp; blinds</strong> — decorative bottom fringe</li>
                                            <li><strong>Lampshades</strong> — fringe trim around the bottom rim</li>
                                            <li><strong>Wall hangings &amp; macramé</strong> — boho art installations</li>
                                            <li><strong>Boot tops &amp; shoe accents</strong> — fashion footwear trim</li>
                                            <li><strong>Event &amp; wedding décor</strong> — table runners, mandap trim, gift wrapping</li>
                                        </ul>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">How do I attach pom pom fringe to fabric or a garment?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Attachment Guide</span>
                                        <p itemProp="text">Pom pom fringe is attached via the header tape at the top of the trim:</p>
                                        <ol>
                                            <li>
                                                <strong>Sew-on (most durable)</strong> — Machine stitch or hand stitch along the
                                                top edge of the header tape. Use a zipper foot to sew close to the base of the
                                                pom poms without catching them. Best for all washable garments.
                                            </li>
                                            <li>
                                                <strong>Iron-on</strong> — Apply heat-bond adhesive strips to the header tape and
                                                press at medium heat for curtains, bags, and non-washable décor items.
                                            </li>
                                            <li>
                                                <strong>Fabric glue</strong> — Apply along the header tape for craft and
                                                decorative applications where washing is not required.
                                            </li>
                                        </ol>
                                        <p>Always <strong>sew-on for washable garments</strong> — adhesives weaken with repeated washing.</p>
                                    </div>
                                </details>
                            </article>

                        </div>

                        {/* ── Wholesale & Custom ── */}
                        <h3 className="pp-cat-heading">
                            <span className="pp-cat-dot" aria-hidden="true" />
                            Wholesale &amp; Custom Orders
                        </h3>
                        <div className="pp-faq-grid speakable-faq">

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">What is the minimum order quantity for wholesale fringes?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">MOQ &amp; Pricing</span>
                                        <p itemProp="text">
                                            Fringes are sold by the metre. <strong>Standard catalogue fringes:</strong> MOQ 10 metres
                                            per colour. <strong>Custom Pantone colours or non-standard sizes:</strong> MOQ 20 metres.
                                        </p>
                                        <p>
                                            Volume price breaks at <strong>20m, 50m, 100m, 250m, and 500m+</strong>. Private label
                                            and custom packaging (rolls with header cards) available for 100m+ orders. Contact us
                                            for a full wholesale pricing sheet.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">Can I order fringes in custom colors?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Custom Colors</span>
                                        <p itemProp="text">
                                            <strong>Yes — custom colour fringes are one of our most popular services.</strong>
                                            We offer 200+ standard colours and full Pantone colour matching. To order:
                                        </p>
                                        <ol>
                                            <li>Share your <strong>Pantone code, fabric swatch, or reference image</strong></li>
                                            <li>We produce a <strong>colour-match sample</strong> within 5–7 business days</li>
                                            <li>You approve the sample and confirm the order</li>
                                            <li>Production begins — both the pom poms and header tape can be colour-coordinated</li>
                                        </ol>
                                        <p>Minimum 20 metres per custom colour.</p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">Do you ship fringes internationally?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">International Shipping</span>
                                        <p itemProp="text">
                                            <strong>Yes — we ship to 50+ countries worldwide.</strong> Fringes are wound onto
                                            cardboard rolls and packed in boxes to protect shape in transit. All orders include
                                            a tracking number. Shipping options:
                                        </p>
                                        <ul>
                                            <li><strong>DHL Express</strong> — 3–5 business days internationally</li>
                                            <li><strong>FedEx International</strong> — 4–7 business days</li>
                                            <li><strong>Registered Airmail</strong> — 10–21 business days (economy)</li>
                                        </ul>
                                        <p>
                                            Regular buyers in the <strong>USA, UK, Canada, Australia, UAE, Germany, France</strong>,
                                            and across Europe and South-East Asia. Import duties are the buyer's responsibility.
                                        </p>
                                    </div>
                                </details>
                            </article>

                            <article className="pp-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                <details>
                                    <summary>
                                        <div className="pp-faq-q-wrap">
                                            <div className="pp-faq-icon">{FAQ_ICON}</div>
                                            <h4 className="pp-faq-question" itemProp="name">Are your faux fur pom pom fringes cruelty-free?</h4>
                                        </div>
                                        <div className="pp-faq-toggle">{TOGGLE_ICON}</div>
                                    </summary>
                                    <div className="pp-faq-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                        <span className="pp-faq-tag">Ethics &amp; Sustainability</span>
                                        <p itemProp="text">
                                            <strong>Yes — all faux fur pom pom fringes are 100% cruelty-free and vegan.</strong>
                                            Made entirely from synthetic acrylic and polyester fibres with zero animal content.
                                            No real fur, no down, and no animal-derived materials of any kind.
                                        </p>
                                        <p>
                                            Our wool fringes use ethically sourced, mulesing-free natural wool. Material
                                            composition documentation is available on request for brands requiring compliance
                                            certification.
                                        </p>
                                    </div>
                                </details>
                            </article>

                        </div>
                    </section>

                    {/* ── RELATED PRODUCTS ── */}
                    <section className="pp-about-grid" style={{ borderTop: '1px solid var(--pp-border)', paddingTop: 'clamp(40px, 6vw, 72px)' }} aria-labelledby="fr-related-h2">
                        <div className="pp-about-prose">
                            <h2 id="fr-related-h2">Related Products</h2>
                            <p>
                                Our fringes share their pom pom construction with our standalone
                                <strong> pom pom range</strong>. Many buyers combine fringe trim with
                                <strong> pom pom tassels</strong> and <strong>beaded patches</strong> into
                                a single shipment — covering garment hems, necklines, and motif embellishments
                                in one order to reduce freight costs.
                            </p>
                            <p>
                                All products are handmade in our New Delhi workshop by the same skilled artisans —
                                ensuring consistent quality across your entire order.
                            </p>
                            <div className="pp-hero-cta" style={{ justifyContent: 'flex-start', marginTop: '20px' }}>
                                <a href="/products/pom-poms" className="pp-btn pp-btn-gold">
                                    View Pom Pom Range
                                </a>
                                <a href="/products" className="pp-btn pp-btn-outline" style={{ color: 'var(--pp-warm-brown)', borderColor: 'var(--pp-border-strong)' }}>
                                    All Products
                                </a>
                            </div>
                        </div>
                        <div className="pp-specs-list" aria-label="Related product categories">
                            {[
                                { icon: '🌺', label: 'Faux Fur Pom Poms', value: 'Standalone fluffy pom poms for hats & accessories' },
                                { icon: '🧶', label: 'Wool Pom Poms', value: 'Classic handspun wool pom poms, 1–25 cm' },
                                { icon: '✨', label: 'Metallic Pom Poms', value: 'Gold, silver & rose gold festive pom poms' },
                                { icon: '🌼', label: 'Tassels', value: 'Pom pom tassels in silk, wool & faux fur' },
                                { icon: '💎', label: 'Beaded Patches', value: 'Hand-embroidered bead appliqués & motifs' },
                            ].map(s => (
                                <div key={s.label} className="pp-spec-row">
                                    <div className="pp-spec-icon" aria-hidden="true">{s.icon}</div>
                                    <div className="pp-spec-text">
                                        <strong>{s.label}</strong>
                                        <span>{s.value}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ── CTA ── */}
                    <section className="pp-cta-banner" aria-labelledby="fr-cta-h2">
                        <h2 id="fr-cta-h2">Ready to Order Handmade Fringes?</h2>
                        <p>
                            Get a free wholesale quote within 24 hours. Custom colors, pom pom sizes,
                            bulk metre orders, and private label packaging available. Trusted by fashion
                            and home textile brands in 50+ countries.
                        </p>
                        <div className="pp-cta-buttons">
                            <a href="/request-a-quote" className="pp-btn pp-btn-gold">
                                Request a Wholesale Quote
                            </a>
                            <a href="/contact" className="pp-btn pp-btn-outline">
                                Ask a Question
                            </a>
                        </div>
                    </section>

                </div>
            </main>
        </div>
    );
};

export default FringesPage;
